/**
 * Motor SLA v1 — horario laboral CDMX (lun–vie 09:00–18:00).
 */

export const SLA_TARGETS = {
  p1: { responseMin: 30, resolveMin: 4 * 60, responseLabel: '30 min', resolveLabel: '4 h' },
  p2: { responseMin: 60, resolveMin: 8 * 60, responseLabel: '1 h', resolveLabel: '8 h' },
  p3: { responseMin: 4 * 60, resolveMin: 2 * 8 * 60, responseLabel: '4 h', resolveLabel: '2 d háb' },
  p4: { responseMin: 8 * 60, resolveMin: 5 * 8 * 60, responseLabel: '8 h', resolveLabel: '5 d háb' }
};

const TZ = 'America/Mexico_City';
const DAY_START = 9;
const DAY_END = 18;

function parseDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') return new Date(value);
  if (value?.seconds) return new Date(value.seconds * 1000);
  return new Date(value);
}

function partsInTz(date) {
  const d = parseDate(date);
  if (!d || Number.isNaN(d.getTime())) return null;
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  const parts = fmt.formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  const weekday = get('weekday');
  const dowMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour: Number(get('hour')),
    minute: Number(get('minute')),
    dow: dowMap[weekday] ?? 0
  };
}

function zonedDate({ year, month, day, hour, minute }) {
  const guess = new Date(Date.UTC(year, month - 1, day, hour + 6, minute));
  const p = partsInTz(guess);
  if (!p) return guess;
  const targetUtc = Date.UTC(year, month - 1, day, hour, minute);
  const gotUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
  return new Date(guess.getTime() + (targetUtc - gotUtc));
}

function isBusinessDayParts(p) {
  return p && p.dow >= 1 && p.dow <= 5;
}

function isWithinBusinessHours(p) {
  if (!isBusinessDayParts(p)) return false;
  if (p.hour < DAY_START) return false;
  if (p.hour >= DAY_END) return false;
  return true;
}

function nextBusinessDayParts(p) {
  let y = p.year;
  let m = p.month;
  let d = p.day + 1;
  let guard = 0;
  while (guard++ < 14) {
    const nd = zonedDate({ year: y, month: m, day: d, hour: DAY_START, minute: 0 });
    const np = partsInTz(nd);
    if (isBusinessDayParts(np)) return np;
    d += 1;
    if (d > 31) {
      d = 1;
      m += 1;
    }
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return p;
}

export function alignToBusinessStart(from) {
  const p = partsInTz(from);
  if (!p) return parseDate(from) || new Date();
  if (isWithinBusinessHours(p)) {
    return zonedDate({ year: p.year, month: p.month, day: p.day, hour: p.hour, minute: p.minute });
  }
  if (isBusinessDayParts(p) && p.hour < DAY_START) {
    return zonedDate({ year: p.year, month: p.month, day: p.day, hour: DAY_START, minute: 0 });
  }
  const next = nextBusinessDayParts(p);
  return zonedDate({ year: next.year, month: next.month, day: next.day, hour: DAY_START, minute: 0 });
}

function addBusinessMinutes(from, minutes) {
  let remaining = Math.max(0, Number(minutes) || 0);
  let cursor = alignToBusinessStart(from);
  let guard = 0;
  while (remaining > 0 && guard++ < 500) {
    const p = partsInTz(cursor);
    if (!p || !isBusinessDayParts(p)) {
      const next = nextBusinessDayParts(p || { year: 2026, month: 1, day: 1, dow: 1 });
      cursor = zonedDate({ year: next.year, month: next.month, day: next.day, hour: DAY_START, minute: 0 });
      continue;
    }
    if (p.hour < DAY_START) {
      cursor = zonedDate({ year: p.year, month: p.month, day: p.day, hour: DAY_START, minute: 0 });
      continue;
    }
    if (p.hour >= DAY_END) {
      const next = nextBusinessDayParts(p);
      cursor = zonedDate({ year: next.year, month: next.month, day: next.day, hour: DAY_START, minute: 0 });
      continue;
    }
    const endOfDay = zonedDate({ year: p.year, month: p.month, day: p.day, hour: DAY_END, minute: 0 });
    const slotMin = Math.max(0, (endOfDay.getTime() - cursor.getTime()) / 60000);
    if (remaining <= slotMin) {
      return new Date(cursor.getTime() + remaining * 60000);
    }
    remaining -= slotMin;
    const next = nextBusinessDayParts(p);
    cursor = zonedDate({ year: next.year, month: next.month, day: next.day, hour: DAY_START, minute: 0 });
  }
  return cursor;
}

function normalizePriority(priority) {
  const p = String(priority || 'p3').toLowerCase();
  return SLA_TARGETS[p] ? p : 'p3';
}

export function computeSlaDueDates(createdAt, priority) {
  const start = alignToBusinessStart(createdAt);
  const pri = normalizePriority(priority);
  const targets = SLA_TARGETS[pri];
  return {
    slaResponseDueAt: addBusinessMinutes(start, targets.responseMin).toISOString(),
    slaResolveDueAt: addBusinessMinutes(start, targets.resolveMin).toISOString()
  };
}

export function buildSlaFieldsForCreate(createdAt, priority) {
  const due = computeSlaDueDates(createdAt, priority);
  return {
    ...due,
    slaPausedAt: null,
    slaPausedMs: 0,
    firstResponseAt: null,
    slaBreached: false,
    slaAlertsSent: {}
  };
}

function extendIso(iso, deltaMs) {
  const d = parseDate(iso);
  if (!d || Number.isNaN(d.getTime())) return iso;
  return new Date(d.getTime() + deltaMs).toISOString();
}

export function applySlaPauseResume(ticket, newStatus) {
  const oldStatus = ticket.status || 'open';
  const now = new Date().toISOString();
  const patch = {};

  if (newStatus === 'waiting_client' && oldStatus !== 'waiting_client') {
    patch.slaPausedAt = now;
  }

  if (oldStatus === 'waiting_client' && newStatus !== 'waiting_client') {
    const pausedAt = parseDate(ticket.slaPausedAt);
    if (pausedAt) {
      const deltaMs = Date.now() - pausedAt.getTime();
      patch.slaResponseDueAt = extendIso(ticket.slaResponseDueAt, deltaMs);
      patch.slaResolveDueAt = extendIso(ticket.slaResolveDueAt, deltaMs);
      patch.slaPausedAt = null;
      patch.slaPausedMs = (Number(ticket.slaPausedMs) || 0) + deltaMs;
    }
  }

  return patch;
}

export function recalcSlaOnPriorityChange(ticket, newPriority) {
  if (!ticket.createdAt) return {};
  return computeSlaDueDates(ticket.createdAt, newPriority);
}

function slaPct(startIso, dueIso, nowMs, pausedAtIso) {
  const start = parseDate(startIso)?.getTime();
  const due = parseDate(dueIso)?.getTime();
  if (!start || !due || due <= start) return 0;
  const paused = pausedAtIso ? parseDate(pausedAtIso)?.getTime() : null;
  const now = paused && paused < nowMs ? paused : nowMs;
  return Math.round(((now - start) / (due - start)) * 100);
}

function slaLevel(pct, met, breached) {
  if (met) return 'met';
  if (breached || pct >= 100) return 'breached';
  if (pct >= 90) return 'critical';
  if (pct >= 75) return 'warn';
  return 'ok';
}

function formatSlaRemaining(dueIso, nowMs, pausedAtIso) {
  const due = parseDate(dueIso)?.getTime();
  if (!due) return '—';
  const paused = pausedAtIso ? parseDate(pausedAtIso)?.getTime() : null;
  const now = paused && paused < nowMs ? paused : nowMs;
  const diffMin = Math.round((due - now) / 60000);
  if (diffMin <= 0) return 'Vencido';
  if (diffMin < 60) return `${diffMin} min`;
  if (diffMin < 24 * 60) return `${Math.round(diffMin / 60)} h`;
  return `${Math.round(diffMin / (60 * 24))} d`;
}

export function getSlaSnapshot(ticket, now = new Date()) {
  const createdAt = ticket.createdAt;
  const priority = normalizePriority(ticket.priority);
  const pri = SLA_TARGETS[priority] || SLA_TARGETS.p3;
  let slaResponseDueAt = ticket.slaResponseDueAt;
  let slaResolveDueAt = ticket.slaResolveDueAt;

  if (!slaResponseDueAt && createdAt) {
    const due = computeSlaDueDates(createdAt, priority);
    slaResponseDueAt = due.slaResponseDueAt;
    slaResolveDueAt = due.slaResolveDueAt;
  }

  const clockStart = alignToBusinessStart(createdAt || now).toISOString();
  const nowMs = now.getTime();
  const pausedAt = ticket.status === 'waiting_client' ? ticket.slaPausedAt : null;
  const firstResponseAt = ticket.firstResponseAt || null;
  const resolved = ['resolved', 'closed'].includes(ticket.status || 'open');

  const responseMet =
    Boolean(firstResponseAt) &&
    parseDate(firstResponseAt)?.getTime() <= parseDate(slaResponseDueAt)?.getTime();
  const responseBreached =
    !responseMet &&
    Boolean(slaResponseDueAt) &&
    nowMs > parseDate(slaResponseDueAt)?.getTime() &&
    !pausedAt;

  const responsePct = responseMet
    ? 100
    : slaPct(clockStart, slaResponseDueAt, nowMs, pausedAt);

  const resolveMet =
    resolved &&
    parseDate(ticket.resolvedAt || ticket.updatedAt)?.getTime() <=
      parseDate(slaResolveDueAt)?.getTime();
  const resolveBreached =
    !resolveMet &&
    Boolean(slaResolveDueAt) &&
    nowMs > parseDate(slaResolveDueAt)?.getTime() &&
    !resolved &&
    !pausedAt;

  const resolvePct = resolveMet ? 100 : slaPct(clockStart, slaResolveDueAt, nowMs, pausedAt);

  const response = {
    label: '1.ª respuesta',
    targetMin: pri.responseMin,
    dueAt: slaResponseDueAt,
    pct: responsePct,
    level: slaLevel(responsePct, responseMet, responseBreached),
    remaining: formatSlaRemaining(slaResponseDueAt, nowMs, pausedAt),
    met: responseMet,
    breached: responseBreached,
    active: !firstResponseAt && !resolved
  };

  const resolve = {
    label: 'Resolución',
    targetMin: pri.resolveMin,
    dueAt: slaResolveDueAt,
    pct: resolvePct,
    level: slaLevel(resolvePct, resolveMet, resolveBreached),
    remaining: formatSlaRemaining(slaResolveDueAt, nowMs, pausedAt),
    met: resolveMet,
    breached: resolveBreached,
    active: Boolean(firstResponseAt) && !resolved
  };

  const primary = response.active ? response : resolve;

  return {
    response,
    resolve,
    primary,
    overallLevel: primary.level,
    overallPct: primary.pct,
    isPaused: ticket.status === 'waiting_client',
    slaResponseDueAt,
    slaResolveDueAt,
    atRisk: ['warn', 'critical', 'breached'].includes(primary.level) && !resolved
  };
}

export function enrichTicketSla(ticket) {
  const sla = getSlaSnapshot(ticket);
  return {
    ...ticket,
    slaResponseDueAt: ticket.slaResponseDueAt || sla.slaResponseDueAt,
    slaResolveDueAt: ticket.slaResolveDueAt || sla.slaResolveDueAt,
    slaBreached: Boolean(ticket.slaBreached) || sla.response.breached || sla.resolve.breached,
    sla
  };
}

export function listSlaAtRisk(tickets, { limit = 8 } = {}) {
  return tickets
    .filter((t) => !['resolved', 'closed'].includes(t.status || 'open'))
    .map((t) => enrichTicketSla(t))
    .filter((t) => t.sla?.atRisk)
    .sort((a, b) => (b.sla?.overallPct || 0) - (a.sla?.overallPct || 0))
    .slice(0, limit);
}

export function computeSlaReport(tickets) {
  const closed = tickets.filter((t) => ['resolved', 'closed'].includes(t.status || 'open'));
  let responseMet = 0;
  let responseTotal = 0;
  let resolveMet = 0;
  const resolveTotal = closed.length;
  let activeAtRisk = 0;

  tickets.forEach((t) => {
    const snap = getSlaSnapshot(t);
    if (snap.response.met || snap.response.breached || t.firstResponseAt) {
      responseTotal += 1;
      if (snap.response.met) responseMet += 1;
    }
    if (snap.resolve.met) resolveMet += 1;
    if (snap.atRisk) activeAtRisk += 1;
  });

  return {
    responseCompliancePct: responseTotal ? Math.round((responseMet / responseTotal) * 100) : null,
    resolveCompliancePct: resolveTotal ? Math.round((resolveMet / resolveTotal) * 100) : null,
    responseSample: responseTotal,
    resolveSample: resolveTotal,
    activeAtRisk,
    total: tickets.length
  };
}

export const SLA_LEVEL_LABELS = {
  ok: 'En tiempo',
  warn: 'Cerca del límite',
  critical: 'Urgente',
  breached: 'Plazo vencido',
  met: 'Cumplido a tiempo'
};

export const SLA_PANEL_TITLE = 'Plazos de atención';
export const SLA_PANEL_TIP =
  'Tiempo máximo para la 1.ª respuesta humana y para resolver el caso, según prioridad (P1–P4). Solo cuenta horario laboral lun–vie 9:00–18:00 CDMX. El reloj se pausa en «Esperando cliente».';

/** Badge compacto en tarjetas de cola */
export function formatSlaRowBadge(sla) {
  if (!sla?.atRisk || !sla.primary) return null;
  const { primary, overallLevel, overallPct } = sla;
  const prefix = primary.label === '1.ª respuesta' ? '1.ª resp.' : 'Resolver';

  if (overallLevel === 'breached' || overallPct >= 100) {
    return primary.label === '1.ª respuesta' ? 'Sin 1.ª respuesta' : 'Resolución vencida';
  }

  return `${prefix}: ${primary.remaining}`;
}

/** Línea secundaria en listas de riesgo (dashboard / reportes) */
export function formatSlaRiskSummary(sla) {
  if (!sla?.primary) return '—';
  const { primary, overallLevel } = sla;
  if (overallLevel === 'breached' || primary.breached) {
    return primary.label === '1.ª respuesta' ? 'Sin 1.ª respuesta' : 'Resolución vencida';
  }
  return `${primary.label}: ${primary.remaining}`;
}

/** Meta de barra SLA en detalle */
export function formatSlaBarMeta(item) {
  if (!item) return '—';
  if (item.met) return 'Cumplido a tiempo';
  if (item.breached) {
    return item.label === '1.ª respuesta' ? 'Sin 1.ª respuesta' : 'Resolución vencida';
  }
  const pct = Math.min(100, Math.max(0, item.pct));
  return `${item.remaining} restantes · ${pct}% del plazo`;
}

export const SLA_LEVEL_COLORS = {
  ok: '#10B981',
  warn: '#F59E0B',
  critical: '#EA580C',
  breached: '#DC2626',
  met: '#10B981'
};
